import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { workOrderApi, inventoryApi, settlementApi, myVendorApi } from '../api';
import {
  INVENTORY_CATEGORIES, INVENTORY_STATUS_COLORS, ITEM_CATEGORIES,
  PRODUCTION_PIPELINE, DATA_PIPELINE,
} from '../constants';

const PAGE_SIZE = 20;

export default function PhotoStudioWorkOrders() {
  const navigate = useNavigate();

  // 월 네비게이터
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const prevMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setPage(1);
  };
  const nextMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setPage(1);
  };

  const monthLabel = (() => {
    const [y, m] = currentMonth.split('-').map(Number);
    return `${y}년 ${m}월`;
  })();

  const monthDateFrom = `${currentMonth}-01`;
  const monthDateTo = (() => {
    const [y, m] = currentMonth.split('-').map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    return `${currentMonth}-${String(lastDay).padStart(2, '0')}`;
  })();

  // 필터
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterVendor, setFilterVendor] = useState('');

  // 데이터
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stageSummary, setStageSummary] = useState<Record<string, number>>({});

  // 인보이스 모달
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceGroup, setInvoiceGroup] = useState<any>(null);
  const [invoiceItemPrices, setInvoiceItemPrices] = useState<Record<number, string>>({});
  const [invoicePdfUrl, setInvoicePdfUrl] = useState<string>('');
  const [invoiceForm, setInvoiceForm] = useState({
    vendorName: '',
    commissionRate: '',
    includeVat: true,
    bankName: '',
    accountNumber: '',
    sendMethod: 'email' as 'email' | 'fax',
    recipientEmail: '',
    recipientFax: '',
    memo: '',
  });
  const [invoiceStep, setInvoiceStep] = useState<'form' | 'done'>('form');
  const [createdSettlementIdx, setCreatedSettlementIdx] = useState<number | null>(null);

  const invoiceItemsTotal = Object.values(invoiceItemPrices).reduce((sum, v) => sum + Number(v || 0), 0);
  const invoiceCommission = invoiceItemsTotal * Number(invoiceForm.commissionRate || 0) / 100;
  const invoiceSupply = invoiceItemsTotal - invoiceCommission;
  const invoiceVat = invoiceForm.includeVat ? Math.round(invoiceSupply * 0.1) : 0;
  const invoiceSettlement = invoiceSupply + invoiceVat;

  const openInvoiceModal = async (group: any) => {
    setInvoiceGroup(group);
    const prices: Record<number, string> = {};
    group.items.forEach((item: any) => {
      const price = item.photoContractItem?.unitPrice || 0;
      prices[item.photoWorkOrderIdx] = String(Number(price));
    });
    setInvoiceItemPrices(prices);

    let bankName = '';
    let accountNumber = '';
    try {
      const vendor = await myVendorApi.get();
      bankName = vendor.bankName || '';
      accountNumber = vendor.accountNumber || '';
    } catch {}

    setInvoiceForm({
      vendorName: group.partnerVendorName || '',
      commissionRate: group.items[0]?.photoContract?.commissionRate || '',
      includeVat: true,
      bankName,
      accountNumber,
      sendMethod: 'email',
      recipientEmail: '',
      recipientFax: '',
      memo: '',
    });
    setInvoiceStep('form');
    setCreatedSettlementIdx(null);
    setShowInvoiceModal(true);
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await inventoryApi.workStatus({
        dateFrom: monthDateFrom,
        dateTo: monthDateTo,
        itemCategory: filterCategory || undefined,
        inventoryStatus: filterStage || undefined,
        customerName: filterCustomer || undefined,
        partnerVendorName: filterVendor || undefined,
        page,
        size: PAGE_SIZE,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('입출고 목록 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [monthDateFrom, monthDateTo, filterCategory, filterStage, filterCustomer, filterVendor, page]);

  const fetchStageSummary = useCallback(async () => {
    try {
      const data = await inventoryApi.stageSummary(filterCategory || undefined);
      // API가 [{ stage, count }] 배열을 반환하므로 Record로 변환
      const map: Record<string, number> = {};
      if (Array.isArray(data)) {
        data.forEach((r: any) => { if (r.stage) map[r.stage] = Number(r.count) || 0; });
      }
      setStageSummary(map);
    } catch {}
  }, [filterCategory]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchStageSummary(); }, [fetchStageSummary]);

  const handleStageChange = async (workOrderIdx: number, targetStage: string) => {
    try {
      await workOrderApi.changeStage(workOrderIdx, targetStage);
      fetchItems();
      fetchStageSummary();
    } catch (err: any) {
      alert(`상태 변경 실패: ${err.message}`);
    }
  };

  const handleGenerateInvoice = async (item: any) => {
    setInvoiceGroup(item);
    setInvoicePdfUrl('');
    setShowInvoiceModal(true);
    // PDF 미리보기 fetch
    if (item.contract?.photoContractIdx) {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`https://api.wedmin.biz/photo-studio/settlement/contract/${item.contract.photoContractIdx}/invoice/preview`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const blob = await res.blob();
        setInvoicePdfUrl(URL.createObjectURL(blob));
      } catch { setInvoicePdfUrl(''); }
    }
  };

  const handleSendWorkOrderInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoiceForm.recipientEmail || !invoiceGroup) return;
    try {
      const settlement = await settlementApi.create({
        photoContractIdx: invoiceGroup.contract?.photoContractIdx,
        partnerVendorIdx: null,
        totalSalesAmount: invoiceGroup.contract?.finalAmount || 0,
        commissionRate: 0,
        memo: `입출고관리에서 자동 생성 - ${invoiceGroup.photoContractItem?.productName}`,
      });
      if (settlement?.photoPartnerSettlementIdx) {
        await settlementApi.sendInvoice(settlement.photoPartnerSettlementIdx, {
          recipientEmail: invoiceForm.recipientEmail,
        });
      }
      alert('인보이스가 발송되었습니다.');
      setShowInvoiceModal(false);
      fetchItems();
      fetchStageSummary();
    } catch (err: any) {
      alert(`인보이스 발송 실패: ${err.message}`);
    }
  };

  const formatDate = (dt?: string) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const allStages = [...PRODUCTION_PIPELINE];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">

        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">입출고관리</h1>
            <p className="text-sm text-gray-600 mt-1">주문제작 상품의 공정 현황을 관리합니다</p>
          </div>
        </div>

        {/* 공정별 현황 요약 */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button onClick={() => { setFilterStage(''); setPage(1); }}
            className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${!filterStage ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            전체 <span className="ml-1 opacity-70">{Object.values(stageSummary).reduce((a: number, b: number) => a + b, 0)}</span>
          </button>
          {allStages.map(stage => (
            <button key={stage} onClick={() => { setFilterStage(filterStage === stage ? '' : stage); setPage(1); }}
              className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${filterStage === stage ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {stage} <span className="ml-1 opacity-70">{stageSummary[stage] || 0}</span>
            </button>
          ))}
        </div>

        {/* 월 네비게이터 + 필터 */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded cursor-pointer"><i className="ri-arrow-left-s-line text-lg"></i></button>
              <span className="font-semibold text-gray-800 min-w-[100px] text-center">{monthLabel}</span>
              <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded cursor-pointer"><i className="ri-arrow-right-s-line text-lg"></i></button>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">품목 전체</option>
                {INVENTORY_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <input type="text" placeholder="고객명 검색" value={filterCustomer}
                onChange={(e) => { setFilterCustomer(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[140px]" />
              <input type="text" placeholder="업체명 검색" value={filterVendor}
                onChange={(e) => { setFilterVendor(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[140px]" />
            </div>
          </div>
        </div>

        {/* 목록 */}
        <div className="text-sm text-gray-600 mb-3">
          총 <span className="font-semibold text-gray-800">{total}</span>건
        </div>

        {loading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center text-gray-400">
            <i className="ri-truck-line text-5xl block mb-3"></i>해당 기간에 작업이 없습니다.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs">
                    <th className="px-4 py-3 text-left font-medium">고객명</th>
                    <th className="px-4 py-3 text-left font-medium">품목</th>
                    <th className="px-4 py-3 text-center font-medium">현재 공정</th>
                    <th className="px-4 py-3 text-center font-medium">공정 이동</th>
                    <th className="px-4 py-3 text-center font-medium">등록일</th>
                    <th className="px-4 py-3 text-left font-medium">비고</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item: any) => {
                    const pipeline = PRODUCTION_PIPELINE;

                    return (
                      <tr key={item.photoWorkOrderIdx} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-4 py-3.5">
                          <div className="flex flex-col cursor-pointer hover:text-blue-600"
                            onClick={() => item.customer?.photoCustomerIdx && navigate(`/photo-studio/customer-detail?id=${item.customer.photoCustomerIdx}`)}>
                            {item.customer?.groomName && <span className="text-sm"><i className="ri-men-line text-blue-500 mr-1"></i>{item.customer.groomName}</span>}
                            {item.customer?.brideName && <span className="text-sm"><i className="ri-women-line text-pink-500 mr-1"></i>{item.customer.brideName}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm font-medium">{item.photoContractItem?.productName || '-'}</span>
                          <div className="text-xs text-gray-400">
                            {ITEM_CATEGORIES.find(c => c.value === item.photoContractItem?.itemCategory)?.label}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${INVENTORY_STATUS_COLORS[item.currentStage] || 'bg-gray-100 text-gray-700'}`}>
                            {item.currentStage}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <select value={item.currentStage}
                            onChange={(e) => handleStageChange(item.photoWorkOrderIdx, e.target.value)}
                            className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500">
                            {pipeline.map(stage => (
                              <option key={stage} value={stage}>{stage}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3.5 text-center text-sm text-gray-500">{formatDate(item.createDt)}</td>
                        <td className="px-4 py-3.5 text-sm">
                          {item.customer?.photoCustomerIdx ? (
                            <button onClick={() => handleGenerateInvoice(item)}
                              className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer flex items-center gap-1">
                              <i className="ri-file-text-line"></i> 인보이스
                            </button>
                          ) : <span className="text-gray-400">{item.memo || '-'}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-sm cursor-pointer ${page === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
        {/* 인보이스 발행 모달 */}
        {showInvoiceModal && invoiceGroup && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">인보이스 발행</h2>
                <button onClick={() => setShowInvoiceModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl"></i></button>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-200">
                {/* 좌측: PDF 미리보기 */}
                <div className="p-5">
                  <p className="text-xs text-gray-400 mb-3 flex items-center gap-1"><i className="ri-eye-line"></i> 인보이스 미리보기</p>
                  {invoicePdfUrl ? (
                    <iframe src={invoicePdfUrl} className="w-full h-[500px] border border-gray-200 rounded-lg" title="인보이스 미리보기" />
                  ) : (
                    <div className="flex items-center justify-center h-[500px] text-gray-400">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mr-2"></div>
                      PDF 생성중...
                    </div>
                  )}
                </div>
                {/* 우측: 발송 정보 */}
                <form onSubmit={handleSendWorkOrderInvoice} className="p-5 space-y-4">
                  <div className="bg-purple-50 rounded-lg p-3 text-sm text-purple-700">
                    <i className="ri-information-line mr-1"></i>
                    정산 요청서(인보이스)가 PDF로 생성되어 이메일로 발송됩니다.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">수신 이메일 *</label>
                    <input type="email" value={invoiceForm.recipientEmail} onChange={(e) => setInvoiceForm({ ...invoiceForm, recipientEmail: e.target.value })}
                      required placeholder="planner@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">메시지 (선택)</label>
                    <textarea value={invoiceForm.message || ''} onChange={(e) => setInvoiceForm({ ...invoiceForm, message: e.target.value })}
                      rows={3} placeholder="인보이스와 함께 전달할 메시지"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  </div>
                  <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                    <button type="button" onClick={() => setShowInvoiceModal(false)}
                      className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer text-sm">취소</button>
                    <button type="submit"
                      className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium cursor-pointer text-sm flex items-center gap-1.5">
                      <i className="ri-mail-send-line"></i> 인보이스 발송
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
